import React from 'react';
import PropTypes from 'prop-types';
import { action, computed, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { locationShape, routerShape, withRouter } from 'react-router';
import * as Sentry from '@sentry/react';

import { withConfig, withI18n } from 'spa/Components/Context';
import { BOOKING_BASE_URL } from 'utils/constants';
import { cn } from 'spa/css';
import autobind from 'autobind-decorator';
import isValidException from 'utils/isValidException';

import Button, {
  ButtonSizeTypes,
  ButtonType,
} from '@urbanmassage/component-library/lib/Button/Button';
import Typography, { TypeVariants } from '@urbanmassage/component-library/lib/Typography/Typography';

import AddressField2021 from 'spa/Components/AddressField2021';
import Tooltip from 'spa/Components/Tooltip';

import './digital/find-appointment-new-for-digital.less';
import './find-appointment-for-next.less';

import logoIcon from 'spa/images/icons/hygiene_roundel.svg';
import logoIconFR from 'spa/images/icons/hygiene_top_fr_white.svg';

const MOBILE_BREAKPOINT_WIDTH = 600;

@withRouter
@inject(({
  store: {
    address,
    basketNew,
    notifications,
    screen,
    tracker,
  },
  selection,
}) =>
  ({
    address,
    basketNew,
    notifications,
    screen,
    tracker,
    selection,
  }))
@observer
@withI18n
@withConfig
class FindAppointmentForNext extends React.Component {
  static propTypes = {
    address: PropTypes.shape({
      error: PropTypes.object,
      isLoading: PropTypes.bool.isRequired,
      load: PropTypes.func.isRequired,
      reset: PropTypes.func,
      response: PropTypes.object,
      search: PropTypes.object,
    }).isRequired,
    basketNew: PropTypes.shape({
      basketsAppointmentsAddress: PropTypes.shape({
        put: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
    customButtonTextDesktop: PropTypes.string,
    customButtonTextMobile: PropTypes.string,
    location: locationShape.isRequired,
    notifications: PropTypes.shape({
      add: PropTypes.func.isRequired,
    }).isRequired,
    roundInputBox: PropTypes.bool,
    router: routerShape.isRequired,
    screen: PropTypes.object.isRequired,
    subTitle: PropTypes.string,
    title: PropTypes.string,
    tracker: PropTypes.shape({
      track: PropTypes.func.isRequired,
    }).isRequired,
    trackingCategory: PropTypes.string,
    trackingLocation: PropTypes.string,
  };

  @observable isInitialLoad = false;
  @observable isSubmittingSearch = false;
  @observable selectedAddress;
  @observable showToolTip = false;

  @autobind
  @action('onSelectAddress - FindAppointmentForNext')
  onSelectAddress(value) {
    this.selectedAddress = value;
  }

  @autobind
  @action('onClickUseAddress - FindAppointmentForNext')
  onClickUseAddress() {
    const {i18n} = this.context;
    const {
      basketNew: {
        basketsAppointmentsAddress,
      },
      notifications,
      router,
      tracker,
      trackingCategory,
      trackingLocation,
    } = this.props;

    this.showToolTip = false;

    if (!this.selectedAddress) {
      this.showToolTip = true;

      return;
    }

    const trackingData = {
      category: trackingCategory,
      label: this.selectedAddress.place_id,
    };

    if (trackingLocation) {
      trackingData.data = {
        page: trackingLocation,
      };
    }

    tracker.track('Clicked search in Find Appointment', trackingData);

    this.isSubmittingSearch = true;

    basketsAppointmentsAddress.put('', this.selectedAddress)
      .then((response) => {
        runInAction(() => {
          router.push(`/${i18n.lc}/${BOOKING_BASE_URL}/services`);
        });
      }, (error) => {
        runInAction(() => {
          if (error.json && error.json.code === 'ADDRESS_NOT_SERVICED') {
            router.push(`/${i18n.lc}/${BOOKING_BASE_URL}/address?notServiced=true`);
          } else {
            if (__DEV__) {
              console.error(error);
            }
            notifications.add({
              id: 'addAddressBasketError',
              type: 'error',
              title: i18n.__error(error),
            });

            // This is unexpected so record it in Sentry
            if (isValidException(error)) {
              Sentry.captureException(error);
            } else {
              Sentry.captureMessage('Find appointment component unhandled error address to basket appointment');
            }
          }

          this.isSubmittingSearch = false;
        });
      }).catch((err) => {
        if (__DEV__) {
          console.error(err);
        }

        // This is unexpected so record it in Sentry
        if (isValidException(err)) {
          Sentry.captureException(err);
        } else {
          Sentry.captureMessage('Find appointment component unhandled error address to basket appointment');
        }
      });
  }

  @autobind
  @action('onClickClearAddress - FindAppointmentForNext')
  onClickClearAddress(event) {
    if (event) event.preventDefault();

    const {
      address,
      tracker,
      trackingCategory,
      trackingLocation,
    } = this.props;

    this.selectedAddress = null;
    this.showToolTip = false;

    address.reset();

    const trackingData = {
      category: trackingCategory,
    };

    if (trackingLocation) {
      trackingData.data = {
        page: trackingLocation,
      };
    }

    tracker.track('Clicked Clear Location', trackingData);
  }

  @autobind
  @action('onCloseToolTip - FindAppointmentForNext')
  onCloseToolTip(event) {
    event.preventDefault();
    this.showToolTip = false;
  }

  @autobind addressFieldRef(addressField) { this.addressField = addressField; }

  @computed get regionTitle() {
    const {i18n} = this.context;

    return i18n._x('A massage at home in an hour', 'Homepage top section');
  }

  @computed get regionSubTitle() {
    const {subTitle} = this.props;
    const {i18n} = this.context;

    if (subTitle) {
      return (
        <Typography
          align="center"
          className="find-appointment-for-next__paragraph"
          component={TypeVariants.body}
          dangerouslySetInnerHTML={{ __html: subTitle }}
          margin="0 auto"
          variant={TypeVariants.h4}
          theme="white"
        />
      );
    }

    return (
      <Typography
        align="center"
        className="find-appointment-for-next__paragraph"
        component={TypeVariants.body}
        margin="0 auto"
        theme="white"
        variant={TypeVariants.h4}
      >
        {i18n._x('Super-convenient treatments delivered by trusted therapists.', 'Homepage top section')}
      </Typography>
    );
  }

  render() {
    const { i18n } = this.context;
    const {
      customButtonTextDesktop,
      customButtonTextMobile,
      roundInputBox,
      screen,
      title,
      trackingCategory,
    } = this.props;
    const { isLoading, search } = this.props.address;
    const logo = i18n.region === 'FR' ? logoIconFR : logoIcon;

    // TODO Replace elements with typography component
    // Need to create new typography variant for these big titles
    return (
      <div className="find-appointment-for-next">
        <img alt="" className="find-appointment-for-next__covid-image" src={logo} />
        {
          title ? (
            <h2 className="find-appointment-for-next__title" dangerouslySetInnerHTML={{ __html: title }} />
          ) : (
            <h2 className="find-appointment-for-next__title">
              {this.regionTitle}
            </h2>
          )
        }
        {this.regionSubTitle}
        <div className="find-appointment-for-next__search-container">
          <div className={cn(
            'row find-appointment-for-next__search-field-container',
            roundInputBox && 'find-appointment-for-next__search-field-container--round',
          )}>
            <div className="find-appointment-for-next__search-field-input-wrapper">
              <AddressField2021
                data-qa="address-field-home"
                className="find-appointment-for-next__search-field"
                clearStyles={{
                  display: 'block',
                  position: 'absolute',
                  top: '30%',
                  right: '1.5rem',
                  height: '2.5rem',
                  width: '2.5rem',
                }}
                defaultValue={search && search.term}
                disabled={isLoading}
                hideClearOnEmpty
                includeClear
                onClear={this.onClickClearAddress}
                onSelect={this.onSelectAddress}
                placeholder={i18n._x('Enter your postcode', 'Home address picker')}
                ref={this.addressFieldRef}
                skipGeolocation
                skipInitialLoad={this.isInitialLoad}
                trackingCategory={trackingCategory}
              />
              {this.showToolTip &&
                <Tooltip
                  align={screen.width > MOBILE_BREAKPOINT_WIDTH ? 'center' : 'top'}
                  containerClassName="find-appointment-for-next__tooltip"
                  hide={!this.showToolTip}
                  onClose={this.onCloseToolTip}
                  theme="light"
                  width={screen.width > MOBILE_BREAKPOINT_WIDTH ? 400 : null}
                >
                  <span>
                    {i18n._x('Please enter your address', 'Find appointment box')}
                  </span>
                </Tooltip>
              }
            </div>
            <div className="find-appointment-for-next__search-button-container">
              <Button
                block
                className="find-appointment-for-next__search-button"
                data-qa="address-search-button-home"
                isLoading={this.isSubmittingSearch}
                onClick={this.onClickUseAddress}
                size={ButtonSizeTypes.large}
                type={ButtonType.button}
              >
                <span className="find-appointment-for-next__search-button-text--mobile">
                  {customButtonTextMobile || i18n._x('Book now', 'homepage top section find appointments')}
                </span>
                <span className={cn(
                  'find-appointment-for-next__search-button-text--desktop',
                  i18n.umRegion === 'FR' && 'find-appointment-for-next__search-button-text--desktop-small',
                )}>
                  {customButtonTextDesktop || i18n._x('Book now', 'homepage top section find appointments')}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default FindAppointmentForNext;




// WEBPACK FOOTER //
// ./spa/Pages/Home2020/FindAppointmentForNext.jsx